import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../Button/Button";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";

export default function ToggleButton({
  loginButtonText,
  loginButtonGradient,
  signupButtonText,
  signupButtonGradient,
  signupTextColor,
  signupBorder,
  signupPadding,
  loginUrl,
  signupUrl,
}) {
  const [navData, setNavData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openTitle, setOpenTitle] = useState(null);

  useEffect(() => {
    fetch("https://api.spadserver.com/api/navbar")
      .then((response) => response.json())
      .then((data) => {
        setNavData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching navbar data:", error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="text-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!navData || navData.length === 0) {
    return <div className="text-center">No navigation data available</div>;
  }

  // Grouping navigation data by title and ensuring no duplicate slugs
  const groupedNavData = navData.reduce((acc, item) => {
    if (!acc[item.title]) {
      acc[item.title] = [];
    }
    const uniqueChildren = item.children.reduce((unique, child) => {
      if (!unique.some((c) => c.slug === child.slug)) {
        unique.push(child);
      }
      return unique;
    }, []);
    acc[item.title].push({ ...item, children: uniqueChildren });
    return acc;
  }, {});

  const toggleTitle = (title) => {
    setOpenTitle((prev) => (prev === title ? null : title));
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Items that should not have 'product/' prefix
  const noHostPrefix = ["دامنه", "درباره اسپاد", "مشتریان ما", "ارتباط با ما"];

  return (
    <div className="font-main bg-custom-gradient border-b-2 border-border">
      <div className="flex justify-between items-center p-4 mx-4 md:mx-10">
        <a href="/">
          <img src="/images/newLogo.png" className="w-16" alt="Logo" />
        </a>
        <div className="wrapper-button flex">
          {/* Mobile Menu Toggle Button */}
          <button
            className="block md:hidden text-white focus:outline-none ml-2"
            onClick={toggleDropdown}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </button>
          <button
            className="block md:hidden text-white focus:outline-none"
            onClick={() => setIsMenuOpen((prev) => !prev)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              ></path>
            </svg>
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        <div
          className={`absolute top-20 left-2 mt-2 text-black shadow-lg rounded-md overflow-hidden transition-opacity duration-300 ${
            isDropdownOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="p-4 space-y-2">
            <a href={loginUrl}>
              <Button
                text={loginButtonText}
                gradient={loginButtonGradient}
                className="w-full py-1 text-xs"
              />
            </a>
            <a href={signupUrl}>
              <Button
                text={signupButtonText}
                gradient={signupButtonGradient}
                textColor={signupTextColor}
                border={signupBorder}
                padding={signupPadding}
                className="w-full py-1 text-xs"
              />
            </a>
          </div>
        </div>

        {/* Centered Desktop Menu */}
        <div className="hidden md:flex items-center text-white font-semibold w-full justify-center">
          {Object.entries(groupedNavData).map(([title, items]) => {
            const slugPrefix = noHostPrefix.includes(title.trim())
              ? ""
              : "product/";

            return (
              <div
                key={title}
                className="relative group mx-4 cursor-pointer"
                onClick={() => toggleTitle(title)}
              >
                <div className="hostLink cursor-pointer text-center">
                  {title}
                </div>
                {openTitle === title && (
                  <div className="navbarSubMenu absolute z-10 top-full left-0 w-64 bg-white p-4 shadow-lg">
                    <div className="grid grid-cols-1 gap-4">
                      {items.map((item) => (
                        <div key={item.id}>
                          <p className="text-gray-700 text-sm font-medium border-b border-gray-300 pb-2">
                            {item.name}
                          </p>
                          <ul className="mt-2">
                            {item.children.map((subItem) => (
                              <li key={subItem.id} className="mt-1">
                                <Link
                                  to={`/${slugPrefix}${subItem.slug}`}
                                  className="text-black text-[11px] hover:bg-gray-100 block font-medium px-2 py-1 rounded-md"
                                  onClick={closeMenu}
                                >
                                  {subItem.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Buttons for desktop */}
        <div className="hidden md:flex space-x-4">
          <a href={loginUrl}>
            <Button text={loginButtonText} gradient={loginButtonGradient} />
          </a>
          <a href={signupUrl}>
            <Button
              text={signupButtonText}
              gradient={signupButtonGradient}
              textColor={signupTextColor}
              border={signupBorder}
              padding={signupPadding}
            />
          </a>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`${
          isMenuOpen ? "block" : "hidden"
        } md:hidden text-white font-semibold bg-custom-gradient`}
      >
        {Object.entries(groupedNavData).map(([title, items]) => {
          const slugPrefix = noHostPrefix.includes(title.trim())
            ? ""
            : "product/";

          return (
            <div key={title} className="px-4 py-2">
              <div
                className="hostLink cursor-pointer text-center"
                onClick={() => toggleTitle(title)}
              >
                {title}
              </div>
              {openTitle === title && (
                <div className="pl-4">
                  {items.map((item) => (
                    <div key={item.id}>
                      <p className="text-gray-200 text-sm font-medium border-b border-gray-700 pb-2">
                        {item.name}
                      </p>
                      <ul className="mt-2">
                        {item.children.map((subItem) => (
                          <li key={subItem.id} className="mt-1">
                            <Link
                              to={`/${slugPrefix}${subItem.slug}`}
                              className="text-white text-[11px] block font-medium px-2 py-1 rounded-md"
                              onClick={closeMenu}
                            >
                              {subItem.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
